<template>
  <v-dialog
    v-model="dialog"
    width="500"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        :color="buttonColor"
        rounded
      >
        <v-icon v-if="icon" left>
          {{ icon }}
        </v-icon>
        {{ title }}
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <span class="headline">{{ title }}</span>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-text-field 
                v-model="name"
                label="Name"
                :rules="nameRules"
                prepend-icon="short_text"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea 
                v-model="description"
                label="Description"
                prepend-icon="mdi-text"
                rows="3"
              ></v-textarea>
            </v-col>
            <v-col cols="12">
              <v-text-field 
                v-model="url"
                label="Sign Up URL"
                :rules="urlRules"
                :prefix="trainingUrl"
                prepend-icon="mdi-link"
                rows="3"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="isManagerTraining" 
                inset 
                label="Manager Training"
                hint="This training is required by all employees with direct reports"
                persistent-hint
                :disabled="disableManagerTraining"
              ></v-switch>
              <v-switch
                v-model="isSexualHarassmentTraining" 
                inset 
                label="Sexual Harassment Training"
                hint="This training is required by all employees every 12 months"
                persistent-hint
                :disabled="disableSexualHarassmentTraining"
              ></v-switch>
              <v-switch
                v-model="isDiversityTraining" 
                inset 
                label="Diversity Training"
                hint="This training is required by all employees"
                persistent-hint
                :disabled="disableDiversityTraining"
              ></v-switch>
              <v-switch
                v-model="isCovid19Training" 
                inset 
                label="COVID-19 Training"
                hint="This training is required by all employees before returning to the office"
                persistent-hint
                :disabled="disableCovid19Training"
              ></v-switch>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-btn 
          v-if="showDeleteButton"
          text
          rounded
          color="error"
          @click="toggleShowDeleteConfirmation"
        >
          Delete Curriculum
        </v-btn>
        <v-btn 
          v-if="showDeleteConfirmationButton"
          color="error"
          rounded
          @click="deleteCurriculum"
          :loading="deleteLoading"
        >
          Really Delete Curriculum?
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn rounded color="blue darken-1" text @click="close">Cancel</v-btn>
        <v-btn 
          color="blue darken-1"
          text
          rounded
          @click="save"
          :disabled="saveDisabled"
          :loading="saveLoading"
        >Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  export default {
    name: 'CurriculumForm',
    props: {
      title: String,
      icon: String,
      method: String,
      curriculum: Object,
      buttonColor: String,
      sessions: Array
    },
    data () {
      return {
        dialog: false,
        saveDisabled: false,
        saveLoading: false,
        showDeleteConfirmationButton: false,
        deleteLoading: false,
        name: '',
        nameRules: [ value => !!value || 'Required' ],
        description: '',
        url: '',
        urlRules: [
          // value => value == 'stop' || 'gotta be stop'
          value => {
            const urlIsAvailable = this.curriculums.filter(c => {
              if (c.id === this.curriculum.id) return false
              return c.fields.URL === value.toLowerCase()
            }).length === 0
            return urlIsAvailable || 'URL is not available'
          }
        ],
        isSexualHarassmentTraining: this.curriculum ? 
                                    this.curriculum
                                    .fields['Is Sexual Harassment Training'] :
                                    false,
        isManagerTraining: this.curriculum ? 
                           this.curriculum.fields['Is Manager Training'] :
                           false,
        isDiversityTraining: this.curriculum ? 
                             this.curriculum.fields['Is Diversity Training'] :
                             false,
        isCovid19Training: this.curriculum ? 
                           this.curriculum.fields['Is COVID-19 Training'] :
                           false,
      }
    },
    created () {
      if (this.$store.getters.getCurriculums.length === 0) {
        this.$store.dispatch('getRecords', {
          table: 'Curriculums',
          view: 'All Curriculums',
          dispatch: 'setCurriculums'
        })
      }
      if (this.curriculum) {
        this.name = this.curriculum.fields.Name
        this.description = this.curriculum.fields.Description
        this.url = this.curriculum.fields.URL
      }
    },
    computed: {
      curriculums () {
        return this.$store.getters.getCurriculums
      },
      trainingUrl () {
        return `${window.location.origin}/training/`
      },
      showDeleteButton () {
        return !this.showDeleteConfirmationButton && this.method === 'edit'
      }, 
      disableSexualHarassmentTraining () {
        return this.isManagerTraining || this.isDiversityTraining ||
               this.isCovid19Training
      },
      disableManagerTraining () {
        return this.isSexualHarassmentTraining || this.isDiversityTraining ||
               this.isCovid19Training
      },
      disableDiversityTraining () {
        return this.isSexualHarassmentTraining || this.isManagerTraining ||
               this.isCovid19Training
      },
      disableCovid19Training () {
        return this.isSexualHarassmentTraining || this.isManagerTraining ||
               this.isDiversityTraining
      }
    },
    methods: {
      toggleShowDeleteConfirmation () {
        return this.showDeleteConfirmationButton = 
               !this.showDeleteConfirmationButton
      },
      deleteCurriculum () {
        this.deleteLoading = true
        // clean up orphaned sessions for this curriculum
        this.$store.dispatch('deleteRecords', {
          table: 'Sessions',
          records: this.sessions
        })
        // delete the curriculum
        this.$store.dispatch('deleteRecords', {
          table: 'Curriculums',
          records: [this.curriculum]
        })
        .then(() => {
          this.showDeleteConfirmationButton = false
          this.deleteLoading = false
          this.$router.replace('/')
        })
      },
      save () {
        this.saveLoading = true
        this.saveDisabled = true
        const fields = {
          'Name': this.name,
          'Description': this.description,
          'Custom URL': this.url,
          'Is Sexual Harassment Training': this.isSexualHarassmentTraining,
          'Is Manager Training': this.isManagerTraining,
          'Is Diversity Training': this.isDiversityTraining,
          'Is COVID-19 Training': this.isCovid19Training
        }
        if (this.method === 'create') {
          this.$store.dispatch('createRecords', {
            table: 'Curriculums',
            records: [{ fields: fields }]
          })
          .then((values) => {
            this.$store.dispatch('addCurriculum', values[0].data[0])
            this.$store.dispatch('setSnackbar', {
              snackbarColor: 'success',
              showSnackbar: true,
              snackbarY: 'top',
              snackbarX: 'right',
              snackbarText: `Created ${this.name}`
            })
            this.saveLoading = false
            this.saveDisabled = false
            this.close()
          })
        } else if (this.method === 'edit') {
          this.$store.dispatch('updateRecords', {
            table: 'Curriculums',
            records: [{ id: this.curriculum.id, fields: fields }]
          })
          .then(() => {
            this.$store.dispatch('getRecords', {
              table: 'Curriculums',
              view: 'All Curriculums',
              dispatch: 'setCurriculums'
            })
            this.$store.dispatch('setSnackbar', {
              snackbarColor: 'success',
              showSnackbar: true,
              snackbarY: 'top',
              snackbarX: 'right',
              snackbarText: `Updated ${this.name}`
            })
            this.saveLoading = false
            this.saveDisabled = false
            this.close()
          })
        }
      },
      close () {
        this.dialog = false
        this.showDeleteConfirmationButton = false
      }
    }
  }
</script>
